import moment from 'moment';

import {
  getApiData,
  getErrorReason,
  hasData,
  isBusinessError,
} from 'scenes/common/helpers/apiData';
import {
  Address,
  BuildingLevel,
  BuildingType,
  CoverType,
  Excess,
  ExcessCode,
  PaymentFrequency,
  PolicyIssued,
  SumInsuredType,
  ValueRange,
  YesNoAnswer,
} from 'scenes/common/types';

import {
  defaultExcess,
  defaultSpecifiedContent,
} from 'scenes/home/flows/coverOptions/defaults';
import { defaultInsurancePremium } from 'scenes/home/flows/quoteDetails/defaults';
import {
  ExcessType,
  HomeFlowType,
  HomeFunnelState,
  InsuranceOption,
  KickOutChoice,
  PropertyPurposeType,
  SpecifiedContent,
} from 'scenes/home/types';

import { SpecifiedContentCover } from '../flows/coverOptions/types';
import { getCampaignAdjustments } from './campaignSelectors';

export const getCoverType = (state: HomeFunnelState): CoverType =>
  state.quoteDetails.coverType;

export const hasBuildingCoverType = (state: HomeFunnelState): boolean =>
  [CoverType.BuildingAndContents, CoverType.Building].includes(
    getCoverType(state)
  );

export const hasContentsCoverType = (state: HomeFunnelState): boolean =>
  [CoverType.BuildingAndContents, CoverType.Contents].includes(
    getCoverType(state)
  );

export const isBuildingAndContentsCoverType = (
  state: HomeFunnelState
): boolean => getCoverType(state) === CoverType.BuildingAndContents;

export const isBuildingCoverType = (state: HomeFunnelState): boolean =>
  getCoverType(state) === CoverType.Building;

export const isContentsCoverType = (state: HomeFunnelState): boolean =>
  getCoverType(state) === CoverType.Contents;

export const getSelectedCoverType = (state: HomeFunnelState) =>
  state.quoteDetails.coverType;

export const getPolicyStartDate = (state: HomeFunnelState): string =>
  state.quoteDetails.policyStartDate;

export const getRiskAddress = (state: HomeFunnelState): Address =>
  state.quoteDetails.riskAddress;

export const getOwnedProperty = (state: HomeFunnelState): YesNoAnswer =>
  state.quoteDetails.ownedProperty;

export const isPropertyOwned = (state: HomeFunnelState): boolean =>
  state.quoteDetails.ownedProperty === YesNoAnswer.Yes;

export const getPurposeOfProperty = (
  state: HomeFunnelState
): PropertyPurposeType => state.quoteDetails.purposeOfProperty;

export const isInvestmentProperty = (state: HomeFunnelState): boolean =>
  state.quoteDetails.purposeOfProperty === PropertyPurposeType.Investment;

export const getKickOutChoice = (state: HomeFunnelState): KickOutChoice =>
  state.quoteDetails.kickOutChoice;

export const notInvestmentProperty = (state: HomeFunnelState): boolean =>
  getKickOutChoice(state) === KickOutChoice.ChangeOfMind;

export const getPropertyUsedForBusiness = (
  state: HomeFunnelState
): YesNoAnswer => state.quoteDetails.propertyUsedForBusiness;

export const isPropertyUsedForBusiness = (state: HomeFunnelState): boolean =>
  state.quoteDetails.propertyUsedForBusiness === YesNoAnswer.Yes;

export const getPropertyBusinessType = (state: HomeFunnelState): string =>
  state.quoteDetails.propertyBusinessType;

export const getPolicyIssued = (state: HomeFunnelState): PolicyIssued =>
  state.quoteDetails.policyIssued ?? PolicyIssued.Unspecified;

export const isPolicyIssuedToPerson = (state: HomeFunnelState): boolean =>
  getPolicyIssued(state) === PolicyIssued.Person;

export const getPolicyHolderDateOfBirth = (state: HomeFunnelState): string =>
  state.quoteDetails.policyHolderDateOfBirth;

export const getBusinessOwnerDateOfBirth = (state: HomeFunnelState): string =>
  state.quoteDetails.businessOwnerDateOfBirth;

export const getRecentInsurer = (state: HomeFunnelState): string =>
  state.quoteDetails.recentInsurer;

export const getYearsInsured = (state: HomeFunnelState): number =>
  state.quoteDetails.yearsInsured;

export const getClaimsMade = (state: HomeFunnelState): number =>
  state.quoteDetails.claimsMade;

export const hasClaimsMade = (state: HomeFunnelState): boolean =>
  state.quoteDetails.claimsMade > 0;

export const getBrokenInClaims = (state: HomeFunnelState): YesNoAnswer =>
  state.quoteDetails.brokenInClaims;

export const hasBreakInClaims = (state: HomeFunnelState): boolean =>
  getBrokenInClaims(state) === YesNoAnswer.Yes;

export const getAdditionalSecurityInstalled = (
  state: HomeFunnelState
): YesNoAnswer => state.quoteDetails.additionalSecurityInstalled;

export const hasAdditionalSecurityInstalled = (
  state: HomeFunnelState
): boolean => getAdditionalSecurityInstalled(state) === YesNoAnswer.Yes;

export const getBuildingType = (state: HomeFunnelState): BuildingType =>
  state.quoteDetails.buildingType;

export const isUnitBuildingType = (state: HomeFunnelState): boolean =>
  getBuildingType(state) === BuildingType.Unit;

export const getFreestandingMetreGap = (state: HomeFunnelState): YesNoAnswer =>
  state.quoteDetails.freestandingMetreGap;

export const isFreestandingAboveGround = (state: HomeFunnelState): boolean =>
  getFreestandingMetreGap(state) === YesNoAnswer.Yes;

export const getFreestandingStorey = (state: HomeFunnelState): YesNoAnswer =>
  state.quoteDetails.freestandingStorey;

export const isFreestandingMultiStorey = (state: HomeFunnelState): boolean =>
  getFreestandingStorey(state) === YesNoAnswer.Yes;

export const getTerraceStorey = (state: HomeFunnelState): YesNoAnswer =>
  state.quoteDetails.terraceStorey;

export const isTerraceMultiStorey = (state: HomeFunnelState): boolean =>
  getTerraceStorey(state) === YesNoAnswer.Yes;

export const getTerraceStrata = (state: HomeFunnelState): YesNoAnswer =>
  state.quoteDetails.terraceStrata;

export const isTerraceUnderStrata = (state: HomeFunnelState): boolean =>
  getTerraceStrata(state) === YesNoAnswer.Yes;

export const getTownhouseStorey = (state: HomeFunnelState): YesNoAnswer =>
  state.quoteDetails.townhouseStorey;

export const isTownhouseMultiStorey = (state: HomeFunnelState): boolean =>
  getTownhouseStorey(state) === YesNoAnswer.Yes;

export const getTownhouseStrata = (state: HomeFunnelState): YesNoAnswer =>
  state.quoteDetails.townhouseStrata;

export const isTownhouseUnderStrata = (state: HomeFunnelState): boolean =>
  getTownhouseStrata(state) === YesNoAnswer.Yes;

export const getUnitStorey = (state: HomeFunnelState): YesNoAnswer =>
  state.quoteDetails.unitStorey;

export const isUnitMultiStorey = (state: HomeFunnelState): boolean =>
  getUnitStorey(state) === YesNoAnswer.Yes;

export const getUnitLevel = (state: HomeFunnelState): BuildingLevel =>
  state.quoteDetails.unitLevel;

export const getUnitStrata = (state: HomeFunnelState): YesNoAnswer =>
  state.quoteDetails.unitStrata;

export const isUnitUnderStrata = (state: HomeFunnelState): boolean =>
  getUnitStrata(state) === YesNoAnswer.Yes;

export const getNumberOfBedrooms = (state: HomeFunnelState): number =>
  state.quoteDetails.numberOfBedrooms;

export const getPropertyBuiltYear = (state: HomeFunnelState): string =>
  state.quoteDetails.propertyBuiltYear;

export const certificateOfOccupancyAnswerRequired = (buildYear: string) =>
  Number(buildYear) > moment().year() - 2;

export const getCertificateOfOccupancy = (
  state: HomeFunnelState
): YesNoAnswer => state.quoteDetails.certificateOfOccupancy;

export const hasCertificateOfOccupancy = (state: HomeFunnelState): boolean =>
  certificateOfOccupancyAnswerRequired(getPropertyBuiltYear(state)) &&
  getCertificateOfOccupancy(state) === YesNoAnswer.Yes;

export const getRoofMaterial = (state: HomeFunnelState): string =>
  state.quoteDetails.roofMaterial;

export const getWallMaterial = (state: HomeFunnelState): string =>
  state.quoteDetails.wallMaterial;

export const getSecurityFeatures = (state: HomeFunnelState): string[] =>
  state.quoteDetails.securityFeatures;

export const getAgreedSumInsuredRange = (state: HomeFunnelState): ValueRange =>
  state.quoteDetails.agreedSumInsuredRange;

export const getAgreedSumInsured = (state: HomeFunnelState): string =>
  state.quoteDetails.agreedBuildingSumInsured;

export const getAgreedSumInsuredValues = (state: HomeFunnelState) => ({
  agreedBuildingSumInsured: getAgreedSumInsured(state),
  agreedSumInsuredRange: getAgreedSumInsuredRange(state),
});

export const getSumInsuredType = (state: HomeFunnelState): SumInsuredType =>
  state.quoteDetails.sumInsuredType;

export const getEstimatedSumInsured = (state: HomeFunnelState): string =>
  state.quoteDetails.estimatedSumInsured;

export const getSumInsuredChoiceValues = (state: HomeFunnelState) => ({
  sumInsuredType: getSumInsuredType(state),
  estimatedSumInsured: getEstimatedSumInsured(state),
});

export const usingEstimatedSumInsured = (state: HomeFunnelState): boolean =>
  getSumInsuredType(state) === SumInsuredType.Estimated;

export const usingAgreedSumInsured = (state: HomeFunnelState): boolean =>
  getSumInsuredType(state) === SumInsuredType.Agreed;

export const getBuildingSumInsured = (state: HomeFunnelState) =>
  usingEstimatedSumInsured(state)
    ? getEstimatedSumInsured(state)
    : getAgreedSumInsured(state);

export const getEstimatedBuildingSumInsured = (state: HomeFunnelState) =>
  getEstimatedSumInsured(state);

export const getContentsSumInsured = (state: HomeFunnelState): string =>
  state.quoteDetails.contentsSumInsured;

export const hasFlybuys = (state: HomeFunnelState): YesNoAnswer =>
  state.quoteDetails.hasFlybuys;

export const isFlybuysMember = (state: HomeFunnelState): boolean =>
  state.quoteDetails.hasFlybuys === YesNoAnswer.Yes;

export const getFlybuysNumber = (state: HomeFunnelState): string =>
  (isFlybuysMember(state) && state.quoteDetails.flybuysNumber) || '';

export const getQuoteNumber = (state: HomeFunnelState): string => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return getApiData(state.apiResponses.quoteDetails).payload.policy.number;
  }
  if (isBusinessError(state.apiResponses.quoteDetails)) {
    return getErrorReason(state.apiResponses.quoteDetails)
      .quoteNumber as string;
  }
  return '';
};

export const hasQuoteNumber = (state: HomeFunnelState): boolean =>
  Boolean(getQuoteNumber(state));

export const getSaveQuoteEmailAddress = (state: HomeFunnelState): string => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return (
      getApiData(state.apiResponses.quoteDetails).saveQuoteEmailAddress || ''
    );
  }
  return '';
};

export const getSelectedBuildingExcess = (state: HomeFunnelState): Excess => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return (
      getApiData(state.apiResponses.quoteDetails).userSelections
        ?.buildingExcess ?? defaultExcess
    );
  }
  return defaultExcess;
};

export const getSelectedContentsExcess = (state: HomeFunnelState): Excess => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return (
      getApiData(state.apiResponses.quoteDetails).userSelections
        ?.contentsExcess ?? defaultExcess
    );
  }
  return defaultExcess;
};

export const getSelectedExcesses = (state: HomeFunnelState): Excess[] => {
  const excesses = [];
  if (hasBuildingCoverType(state)) {
    excesses.push(getSelectedBuildingExcess(state));
  }

  if (hasContentsCoverType(state)) {
    excesses.push(getSelectedContentsExcess(state));
  }

  return excesses;
};

export const getBuildingExcesses = (state: HomeFunnelState) => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return getApiData(state.apiResponses.quoteDetails)?.buildingExcesses ?? [];
  }
  return [];
};

export const getStandardBuildingExcesses = (state: HomeFunnelState) =>
  getBuildingExcesses(state).filter(
    (excess) => excess.type === ExcessType.Standard
  );

export const getAdditionalBuildingExcesses = (state: HomeFunnelState) =>
  getBuildingExcesses(state).filter(
    (excess) => excess.type === ExcessType.Additional
  );

export const getContentsExcesses = (state: HomeFunnelState) => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return getApiData(state.apiResponses.quoteDetails)?.contentsExcesses ?? [];
  }
  return [];
};

export const getStandardContentsExcesses = (state: HomeFunnelState) =>
  getContentsExcesses(state).filter(
    (excess) => excess.type === ExcessType.Standard
  );

export const getAdditionalContentsExcesses = (state: HomeFunnelState) =>
  getContentsExcesses(state).filter(
    (excess) => excess.type === ExcessType.Additional
  );

export const getUnspecifiedPortableLimitRanges = (state: HomeFunnelState) => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return getApiData(state.apiResponses.quoteDetails)
      .unspecifiedPortableLimitRanges;
  }
  return [];
};

export const getSelectedInsuranceOption = (
  state: HomeFunnelState
): InsuranceOption =>
  getApiData(state.apiResponses.quoteDetails)?.insuranceOptions?.find(
    (opt) => opt.coverType === getSelectedCoverType(state)
  ) ||
  getApiData(state.apiResponses.quoteDetails)?.insuranceOptions?.[0] ||
  defaultInsurancePremium;

export const getSelectedExtras = (state: HomeFunnelState): string[] =>
  state.coverOptions.selectedExtras;

export const getIncludedExtras = (state: HomeFunnelState): string[] => [
  ...getCampaignAdjustments(state)
    .filter(({ percentageValue }) => percentageValue === 100)
    .map(({ type }) => type),
];

export const getExtras = (state: HomeFunnelState): string[] => [
  ...getSelectedExtras(state),
  ...getIncludedExtras(state),
];

export const getSpecifiedContentsAnswer = (
  state: HomeFunnelState
): YesNoAnswer => state.coverOptions.specifiedContentsAnswer;

export const getSpecifiedContents = (
  state: HomeFunnelState
): SpecifiedContent[] => state.coverOptions.specifiedContents;

export const getSpecifiedContentsHomeOnly = (
  state: HomeFunnelState
): SpecifiedContent[] =>
  getSpecifiedContents(state).filter(
    ({ cover }) => cover === SpecifiedContentCover.Home
  );

export const hasSpecifiedContentsHomeOnly = (state: HomeFunnelState): boolean =>
  getSpecifiedContentsHomeOnly(state).length > 0;

export const getSpecifiedPortableContentsAustraliaWide = (
  state: HomeFunnelState
): SpecifiedContent[] =>
  getSpecifiedContents(state).filter(
    ({ cover }) => cover === SpecifiedContentCover.PortableContents
  );

export const hasSpecifiedPortableContentsAustraliaWide = (
  state: HomeFunnelState
): boolean => getSpecifiedPortableContentsAustraliaWide(state).length > 0;

export const hasSpecifiedContents = (state: HomeFunnelState) =>
  getSpecifiedContents(state).length > 0;

export const getSpecifiedContent = (state: HomeFunnelState): SpecifiedContent =>
  state.coverOptions.specifiedContent || defaultSpecifiedContent;

export const getUnspecifiedPortableContents = (
  state: HomeFunnelState
): YesNoAnswer => state.coverOptions.unspecifiedPortableContents;

export const hasUnspecifiedPortableContents = (
  state: HomeFunnelState
): boolean => getUnspecifiedPortableContents(state) === YesNoAnswer.Yes;

export const getUnspecifiedPortableContentsLimit = (
  state: HomeFunnelState
): string => state.coverOptions.unspecifiedPortableContentsLimit;

export const getFirstInstalment = (state: HomeFunnelState): string => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return (
      getApiData(state.apiResponses.quoteDetails).recalculatedInstalments
        ?.firstInstalment ??
      getSelectedInsuranceOption(state)?.premium.firstInstalment ??
      ''
    );
  }
  return '';
};

export const getMonthlyInstalment = (state: HomeFunnelState): string => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return (
      getApiData(state.apiResponses.quoteDetails).recalculatedInstalments
        ?.monthlyInstalment ??
      getSelectedInsuranceOption(state)?.premium.monthlyInstalment ??
      ''
    );
  }
  return '';
};

export const getPrefilledCampaignId = (state: HomeFunnelState): string =>
  state.quoteDetails.prefilledCampaignId;

export const getColesCampaignCode = (state: HomeFunnelState): string =>
  state.quoteDetails.colesCampaignCode;

export const getPrefilledPromoCode = (state: HomeFunnelState): string =>
  state.quoteDetails.prefilledPromoCode;

export const getPromoCode = (state: HomeFunnelState): string =>
  state.quoteDetails.promoCode;

export const hasPromoCode = (state: HomeFunnelState): boolean =>
  getPromoCode(state).length !== 0;

export const getReceiptNumber = (state: HomeFunnelState): string => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return getApiData(state.apiResponses.quoteDetails).receiptNumber ?? '';
  }
  return '';
};

export const getQuoteReference = (state: HomeFunnelState): string => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return (
      getApiData(state.apiResponses.quoteDetails).payload.policy?.reference ||
      ''
    );
  }
  return '';
};

export const getPreselectedPaymentFrequency = (
  state: HomeFunnelState
): PaymentFrequency => {
  if (state.flow === HomeFlowType.CoverOptions) {
    return state.coverOptions.preselectedPaymentFrequency;
  }
  if (state.flow === HomeFlowType.PolicyDetails) {
    return state.policyDetails.preselectedPaymentFrequency;
  }
  return PaymentFrequency.Unspecified;
};

export const isMonthlyPaymentPreselected = (state: HomeFunnelState): boolean =>
  getPreselectedPaymentFrequency(state) === PaymentFrequency.Monthly;

export const getPreselectedPrice = (state: HomeFunnelState): string => {
  const {
    premium: { annualPrice, monthlyInstalment },
  } = getSelectedInsuranceOption(state);
  return isMonthlyPaymentPreselected(state) ? monthlyInstalment : annualPrice;
};

export const getIsPortableContentsExtraSelected = (
  state: HomeFunnelState
): boolean => state.coverOptions.isPortableContentsExtraSelected;

export const getPortablesExcesses = (state: HomeFunnelState) => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return getApiData(state.apiResponses.quoteDetails)?.portablesExcesses ?? [];
  }
  return [];
};

export const getSpecifiedPortablesExcessAmount = (
  state: HomeFunnelState
): number => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return (
      getPortablesExcesses(state).find(
        (excess) => excess.code === ExcessCode.SpecifiedPortablesExcess
      )?.amount ?? 0
    );
  }
  return 0;
};

export const getUnspecifiedPortablesExcessAmount = (
  state: HomeFunnelState
): number => {
  if (hasData(state.apiResponses.quoteDetails)) {
    return (
      getPortablesExcesses(state).find(
        (excess) => excess.code === ExcessCode.UnspecifiedPortablesExcess
      )?.amount ?? 0
    );
  }
  return 0;
};
